/**
 * Screen-local API module for the CTO Common report (task 073-02, epic 073
 * D1–D4) — the between-traffics common-leads view behind
 * `GET /admin/reports/cto-common-stats`.
 *
 * Codes against task 073-01's api-local DTOs (`cto-common.service.ts`,
 * committed in 026b2a30), mirrored here per r1 M-2: the SPA never imports api
 * schemas — these interfaces are the copy, the `cto-stats-api.ts` precedent.
 *
 * Downloads: per-source unique/all leads are LINKS to the EXISTING
 * `cto-leads.csv` route with `siteId` + `variant=all|unique` + range +
 * `includeTest` (r1 H-2 — the CTO Email screen's own export, no new route);
 * the whole-report stats export is task 01's `cto-common-stats.csv`, same
 * predicates as the stats request.
 */

import { apiFetch } from '../../lib/api';
import { ctoLeadsExportUrl } from './cto-stats-api';

/** The six measures of the view — the same shape at the source and the total grain. */
export interface CtoCommonMeasures {
  /** Lead rows of this source in range (`COUNT(DISTINCT l.id)` under the overlap join). */
  leads: number;
  /** Distinct addresses among this source's leads (Q82: source = `campaign_link.site_id`). */
  uniqueLeads: number;
  /** Distinct addresses also captured in-range under a DIFFERENT selected source (epic 073 D2). */
  commonLeads: number;
  /** The verdict splits, copied from cto-stats' `LEAD_MEASURES` (D3). */
  success: number;
  duplicate: number;
  unchecked: number;
}

/** One traffic-source (site) row of `GET /admin/reports/cto-common-stats`. */
export interface CtoCommonSourceRow extends CtoCommonMeasures {
  siteId: string; // site.id::text — the per-source selection value
  siteCode: string; // site.site_id — her "Site ID"
  label: string; // site.label — her "Traffic Source"
}

/** The selection footer: the `()` grouping set's recomputed measures — never the column sums. */
export type CtoCommonTotals = CtoCommonMeasures;

export interface CtoCommonStatsResponse {
  from: string;
  to: string;
  timezone: string;
  includeTest: boolean;
  sources: CtoCommonSourceRow[]; // ORDER BY label, siteCode — also the sources control's options
  totals: CtoCommonTotals;
}

/**
 * Screen filter state. `siteIds` is the multi-source selection — an EMPTY
 * array (or omitted) means every source, the Select-All case (epic 073 D1);
 * a non-empty array is sent as the repeatable `siteIds` key.
 */
export interface CtoCommonStatsFilters {
  from: string;
  to: string;
  includeTest: boolean;
  siteIds?: string[];
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * `GET /admin/reports/cto-common-stats` query — `from`/`to`/`includeTest`
 * always sent; the selection as repeated `siteIds` keys, omitted when empty.
 */
function buildCtoCommonStatsQuery(filters: CtoCommonStatsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  for (const siteId of filters.siteIds ?? []) {
    params.append('siteIds', siteId);
  }
  return params;
}

/** Absolute CTO Common stats URL, ready for `fetch`. */
export function ctoCommonStatsUrl(filters: CtoCommonStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/cto-common-stats?${buildCtoCommonStatsQuery(filters).toString()}`;
}

/** Absolute `cto-common-stats.csv` URL for the whole-report export (task 073-01's route). */
export function ctoCommonStatsExportUrl(filters: CtoCommonStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/cto-common-stats.csv?${buildCtoCommonStatsQuery(filters).toString()}`;
}

/** Per-source lead download variants on this screen — `rejected` is not a control here. */
export type CtoCommonLeadsVariant = 'all' | 'unique';

export interface CtoCommonLeadsExportParams {
  from: string;
  to: string;
  includeTest: boolean;
  siteId: string;
  variant: CtoCommonLeadsVariant;
}

/**
 * Per-source leads download — the EXISTING `cto-leads.csv` (r1 H-2): a thin
 * typed seam over `cto-stats-api`'s builder so this screen can never send
 * `linkId`/`campaignId`/`variant=rejected` (not controls here); the param
 * order stays that builder's.
 */
export function ctoCommonLeadsExportUrl(params: CtoCommonLeadsExportParams): string {
  return ctoLeadsExportUrl({ ...params });
}

/**
 * `message` of a Nest error body without a cast (`reports-api.ts`'s idiom):
 * narrow the unknown JSON with the `in` operator, then the string check.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope -- matches the api's `ApiResponse<T>` (conventions §DTO). */
interface CtoCommonStatsEnvelope {
  data: CtoCommonStatsResponse;
}

/** Fetch the CTO Common stats for one filter set. Non-2xx throws with the body's message (or the status text). */
export async function fetchCtoCommonStats(
  filters: CtoCommonStatsFilters
): Promise<CtoCommonStatsResponse> {
  const response = await apiFetch(ctoCommonStatsUrl(filters));
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new Error(messageFromBody(body, response.statusText));
  }
  const envelope: CtoCommonStatsEnvelope = await response.json();
  return envelope.data;
}
